import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Briefcase, Wand2, CheckCircle2 } from "lucide-react";

const EMPTY = { name: "", start_date: "", end_date: "", description: "", status: "active", store_ids: [], giveaway_stock: {} };

export default function AdminProjects() {
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get("/admin/projects"), api.get("/admin/stores")]);
      setRows(p.data);
      setStores(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name,
      start_date: p.start_date,
      end_date: p.end_date,
      description: p.description || "",
      status: p.status || "active",
      store_ids: p.store_ids || [],
      giveaway_stock: p.giveaway_stock || {},
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.start_date || !form.end_date) {
      toast.error("Start and end dates are required");
      return;
    }
    setSubmitting(true);
    try {
      const giveaway_opening_stock = {};
      form.store_ids.forEach((sid) => {
        const st = stores.find((s) => s.id === sid);
        if (st?.giveaway_applicable) {
          giveaway_opening_stock[sid] = Math.max(0, parseInt(form.giveaway_stock?.[sid], 10) || 0);
        }
      });
      const payload = {
        name: form.name,
        start_date: form.start_date,
        end_date: form.end_date,
        description: form.description,
        status: form.status,
        store_ids: form.store_ids,
        giveaway_opening_stock,
      };
      if (editing) {
        await api.patch(`/admin/projects/${editing}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/admin/projects", payload);
        toast.success("Project created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runMigration = async () => {
    if (!window.confirm("Stamp all legacy shifts/stocks/surveys with a project_id? Safe to re-run.")) {
      return;
    }
    try {
      const r = await api.post("/admin/projects/migrate-legacy");
      const c = r.data?.stamped || {};
      toast.success(
        `Migrated — shifts: ${c.shifts ?? 0}, stocks: ${c.stock_entries ?? 0}, surveys: ${c.surveys ?? 0}`,
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl uppercase tracking-tight">Projects</h1>
          <p className="text-xs font-mono uppercase tracking-widest text-neutral-500 mt-1">
            Campaign / activation windows · shifts auto-assigned by date
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={runMigration}
            className="rounded-xl border-slate-300/70 h-9"
            data-testid="projects-migrate-legacy"
          >
            <Wand2 className="h-4 w-4 mr-1" /> Migrate legacy data
          </Button>
          <Button
            onClick={openCreate}
            className="rounded-xl bg-slate-900 text-white hover:bg-slate-700 h-9"
            data-testid="projects-new"
          >
            <Plus className="h-4 w-4 mr-1" /> New project
          </Button>
        </div>
      </div>

      <div className="glass-panel overflow-x-auto">
        <table className="w-full text-sm" data-testid="projects-table">
          <thead className="bg-white/50 border-b border-slate-200/70">
            <tr>
              {["Name", "Start", "End", "Status", "Stores", "Shifts", "Surveys", "Actions"].map((h) => (
                <th
                  key={h}
                  className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="p-6 text-center text-neutral-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-6 text-center text-neutral-500">
                  No projects yet. Create one to start grouping data by campaign.
                </td>
              </tr>
            ) : (
              rows.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-slate-200/70 hover:bg-white/50"
                  data-testid={`project-row-${p.id}`}
                >
                  <td className="p-3 font-medium flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-neutral-500" />
                    {p.name}
                  </td>
                  <td className="p-3 font-mono">{p.start_date}</td>
                  <td className="p-3 font-mono">{p.end_date}</td>
                  <td className="p-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                        p.status === "active"
                          ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                          : "border-slate-300/70 text-neutral-600"
                      }`}
                    >
                      {p.status === "active" && <CheckCircle2 className="h-3 w-3" />}
                      {p.status}
                    </span>
                  </td>
                  <td className="p-3 font-mono">{(p.store_ids || []).length}</td>
                  <td className="p-3 font-mono">{p.shift_count ?? 0}</td>
                  <td className="p-3 font-mono">{p.survey_count ?? 0}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-xl"
                        onClick={() => openEdit(p)}
                        data-testid={`project-edit-${p.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-xl text-rose-600 hover:bg-rose-50"
                        onClick={() => handleDelete(p)}
                        data-testid={`project-delete-${p.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-md rounded-xl border-slate-300/70 max-h-[88vh] overflow-y-auto"
          data-testid="project-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit project" : "New project"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="rounded-xl border-slate-300/70"
                data-testid="project-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider">Start date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  required
                  className="rounded-xl border-slate-300/70"
                  data-testid="project-start"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-wider">End date</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  required
                  className="rounded-xl border-slate-300/70"
                  data-testid="project-end"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider">
                Description (optional)
              </Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-xl border-slate-300/70"
                rows={2}
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider">
                Assign stores ({form.store_ids.length} selected)
              </Label>
              <p className="text-[11px] text-neutral-500 mb-2 mt-1">
                Stores tied to this project. When a promoter checks into one of these stores, the
                shift will be stamped with this project.
              </p>
              <div className="border border-slate-300/60">
                <div
                  className="max-h-44 overflow-y-auto"
                  data-testid="project-form-stores-scroll"
                >
                  {stores.length === 0 ? (
                    <div className="p-4 text-sm text-neutral-500">
                      No stores. Create one first.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200/50">
                      {stores.map((s) => {
                        const checked = form.store_ids.includes(s.id);
                        return (
                          <div key={s.id}>
                            <label className="flex items-center gap-3 cursor-pointer p-3">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() =>
                                  setForm((f) => ({
                                    ...f,
                                    store_ids: checked
                                      ? f.store_ids.filter((x) => x !== s.id)
                                      : [...f.store_ids, s.id],
                                  }))
                                }
                                className="rounded-xl"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium flex items-center gap-2">
                                  {s.name}
                                  {s.giveaway_applicable && (
                                    <span className="text-[9px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200/70 rounded-full px-1.5 py-0.5">
                                      🎁 giveaway
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs font-mono text-neutral-500 truncate max-w-[300px]">
                                  {s.address}
                                </div>
                              </div>
                            </label>
                            {checked && s.giveaway_applicable && (
                              <div className="flex items-center gap-2 pl-10 pr-3 pb-3 -mt-1">
                                <Label className="text-[11px] text-emerald-700 whitespace-nowrap">
                                  Opening silicone cases
                                </Label>
                                <Input
                                  type="number"
                                  min="0"
                                  className="rounded-xl h-8 w-24"
                                  placeholder="0"
                                  value={form.giveaway_stock?.[s.id] ?? ""}
                                  onChange={(e) =>
                                    setForm((f) => ({
                                      ...f,
                                      giveaway_stock: { ...f.giveaway_stock, [s.id]: e.target.value },
                                    }))
                                  }
                                  data-testid={`project-giveaway-stock-${s.id}`}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider">Status</Label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full rounded-xl border border-slate-300/70 px-3 py-2 text-sm bg-white"
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-xl border-slate-300/70"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
                data-testid="project-submit"
              >
                {submitting ? "Saving…" : editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
